import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../../utils/api';
import Pagination, { getPageSize } from '../../../Pagination';
import type { AwCtx } from '../../ctx';
import { baht, btn, card, dmy, errBox, note, num, pillAmber, pillGreen, pillGrey, pillRed, table, td, th } from '../../ui';
import { Dialog, Foot, field, lbl } from '../../dialog';
import type { FaCategory } from './shared';

// ── Import a whole asset register from a CSV / .xlsx file (batch 9 R4 — COA-H11 + H3) ─────────────
// Preview, then commit, through the same door twice (POST /fixed-assets/assets/import, dryRun first).
// The server runs every row through registerAsset, so this window decides nothing: each row's verdict
// is the server's sentence, shown verbatim. A row with no Category cell of its own takes the category
// chosen here; with neither, the server refuses it (COA-H3). Commit is offered only for the preview
// that is on screen, with nothing refused — the import is all rows or none.
// ⛔ The preview's cost is SATANG (services/assetImport.ts AssetImportRow) and baht() divides by 100
// once; the classic screen divided it twice and showed every cost a hundred times too small.
// ⛔ Every row of the preview can be reached (review R4 #16): it is paged like any list (CLAUDE.md, 100
// a page, Pagination) with the REFUSED rows first. It used to draw the first 200 rows in file order and
// stop, so a register refused on line 250 said "1 refused" over 200 green rows — with Import shut, and
// the reason nowhere on screen.
// ⛔ Every column of the file is on screen above the preview (review R4 #15, follow-up): the ones read,
// each with the field it fills, and — in an amber box nobody scrolls past — every column the import will
// NOT read, with the server's reason (a heading no field answers to, a second column for a field already
// read, values under no heading). A file's 'Useful life (yrs)' used to be dropped without a word, the
// category's rate took its place, and every row still said "Will be registered".

type ImportRow = {
  line: number; nameTh: string; nameEn: string; classCode: string; acquiredDate: string; category: string;
  cost: number; salvage: number; rate: number; error: string; duplicate: boolean;
};
/** services/assetImport.ts IgnoredColumn: `repeats` is the field an earlier column already fills, '' for none. */
type IgnoredColumn = { column: number; header: string; repeats: string };
type ImportReport = {
  rows: ImportRow[]; ok: number; refused: number; created: number;
  columns: Record<string, string>;   // field → the heading it was read from, in file order
  ignored?: IgnoredColumn[];         // optional: a server older than this screen does not send it
};
/** The importer's fields (services/assetImport.ts FIELDS), in the reader's words. A field this screen
 *  does not know yet shows its heading alone, never its code. */
const FIELD_KEY: Record<string, string> = {
  nameTh: 'aw_far_imp_f_name_th', nameEn: 'aw_far_imp_f_name_en', classCode: 'aw_far_imp_f_class', category: 'aw_far_imp_f_category',
  acquiredDate: 'aw_far_imp_f_acquired', cost: 'aw_far_imp_f_cost', salvage: 'aw_far_imp_f_salvage', rate: 'aw_far_imp_f_rate',
  assetAccount: 'aw_far_imp_f_asset_account', accumAccount: 'aw_far_imp_f_accum_account', expenseAccount: 'aw_far_imp_f_expense_account',
  openingAccum: 'aw_far_imp_f_opening_accum', openingAsOf: 'aw_far_imp_f_opening_as_of', note: 'aw_far_imp_f_note',
  vendorName: 'aw_far_imp_f_vendor', vendorInvoiceNo: 'aw_far_imp_f_invoice_no', capitalisedCost: 'aw_far_imp_f_capitalised',
  availableDate: 'aw_far_imp_f_available', depreciationStart: 'aw_far_imp_f_dep_start', location: 'aw_far_imp_f_location',
  status: 'aw_far_imp_f_status', usefulLifeYears: 'aw_far_imp_f_life',
};
const ignoredBox: React.CSSProperties = { ...card, padding: '10px 12px', background: '#FFFBEB', border: '1px solid #F1C232', color: '#92400E' };

export function ImportWindow(p: { ctx: AwCtx; onClose: () => void; onDone: () => void }) {
  const { ctx, onClose, onDone } = p;
  const { t, i18n } = useTranslation();
  const [cats, setCats] = useState<FaCategory[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportReport | null>(null);
  // What the preview on screen was made from — a commit is only offered for exactly that.
  const [previewOf, setPreviewOf] = useState<{ file: File; categoryId: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };

  // The categories an asset can be registered under: switched-off ones are not offered.
  useEffect(() => {
    let alive = true;
    api.faCategories(ctx.clientId).then((x: any) => { if (alive) setCats(((x?.categories || []) as FaCategory[]).filter(c => Number(c.active))); })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e)); });
    return () => { alive = false; };
  }, [ctx.clientId]);

  const catName = (c: FaCategory) => (i18n.language === 'th' ? c.name_th || c.name_en : c.name_en || c.name_th);
  const chosen = cats.find(c => c.id === categoryId) || null;
  const current = !!preview && !!previewOf && previewOf.file === file && previewOf.categoryId === categoryId;
  const ready = current && !!preview && preview.refused === 0 && preview.ok > 0;
  // Refused rows first, each group in file order — so every refusal is on the first page(s).
  const ordered = useMemo(() => (preview ? [...preview.rows].sort((a, b) => (a.error ? 0 : 1) - (b.error ? 0 : 1) || a.line - b.line) : []), [preview]);
  const shown = ordered.slice((page - 1) * limit, page * limit);
  const fieldName = (f: string) => (FIELD_KEY[f] ? t(FIELD_KEY[f]) : '');
  const ignored = preview?.ignored || [];

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setErr('');
    try {
      const r: ImportReport = await api.faImportAssets(ctx.clientId, file, true, categoryId);
      setPreview(r); setPreviewOf({ file, categoryId }); setPage(1);
    } catch (e: any) { setPreview(null); setPreviewOf(null); setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    if (!file || !ready) return;
    setBusy(true); setErr('');
    try {
      await api.faImportAssets(ctx.clientId, file, false, categoryId);
      setDirty(false);
      onDone();
    } catch (e: any) { setErr(String(e?.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog fullscreen title={t('aw_far_imp_title')} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={commit} busy={busy} ready={ready} icon="ti ti-file-import"
        label={preview && current ? t('aw_far_imp_commit', { n: preview.ok }) : t('aw_far_import')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 1100 }} data-fa-import="1">
        <div style={note}>{t('aw_far_imp_help')}</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ flex: '1 1 280px' }}>
            <span style={lbl}>{t('aw_far_imp_cat')}</span>
            <select value={categoryId} onChange={e => ed(setCategoryId, e.target.value)} style={field} aria-label={t('aw_far_imp_cat')} data-fa-imp="category">
              <option value="">{t('aw_far_imp_cat_none')}</option>
              {cats.map(c => <option key={c.id} value={c.id}>{catName(c)}</option>)}
            </select>
          </label>
          <label style={{ flex: '1 1 280px' }}>
            <span style={lbl}>{t('aw_far_imp_file')}</span>
            <input type="file" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={e => ed(setFile, e.target.files?.[0] || null)} style={field} aria-label={t('aw_far_imp_file')} data-fa-imp="file" />
          </label>
          <button type="button" onClick={runPreview} disabled={!file || busy} style={{ ...btn, opacity: !file || busy ? 0.5 : 1 }} data-fa-imp="preview">
            <i className={busy ? 'ti ti-loader' : 'ti ti-eye'} aria-hidden /> {t('aw_far_imp_preview')}
          </button>
        </div>
        {err && <div style={errBox}>{err}</div>}
        {preview && !current && <div style={{ ...card, padding: '9px 12px', fontSize: 12.5, color: '#92400E' }}>{t('aw_far_imp_stale')}</div>}
        {preview && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, opacity: current ? 1 : 0.55 }}>
            {ignored.length > 0 && (
              <div style={ignoredBox} data-fa-imp="ignored">
                <div style={{ fontSize: 13, fontWeight: 800 }}><i className="ti ti-alert-triangle" aria-hidden /> {t('aw_far_imp_ignored', { n: ignored.length })}</div>
                <ul style={{ margin: '6px 0 0', paddingLeft: 20, fontSize: 12.5, lineHeight: 1.6 }}>
                  {ignored.map(c => (
                    <li key={c.column} data-fa-imp-ignored={c.column}>
                      {t('aw_far_imp_column_n', { n: c.column })} <b>{c.header || t('aw_far_imp_no_heading')}</b>
                      {c.repeats && <> — {t('aw_far_imp_twice', { field: fieldName(c.repeats) || c.header })}</>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ ...note, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }} data-fa-imp="columns">
              <span style={{ fontWeight: 700 }}>{t('aw_far_imp_read')}</span>
              {Object.entries(preview.columns || {}).map(([f, h]) => {
                const name = fieldName(f);
                return (
                  <span key={f} style={{ ...pillGrey, fontWeight: 600 }} data-fa-imp-read={f}>
                    {h}{name && name.toLowerCase() !== String(h).toLowerCase() ? ` → ${name}` : ''}
                  </span>
                );
              })}
            </div>
            <div style={{ fontSize: 13, fontWeight: 800, color: preview.refused ? '#B42318' : '#166534' }} data-fa-imp="summary">
              {t('aw_far_imp_summary', { ok: preview.ok, refused: preview.refused })}
            </div>
            {preview.refused > 0 && <div style={note}>{t('aw_far_imp_fix')}</div>}
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={table}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_far_imp_col_line')}</th>
                    <th style={th}>{t('aw_far_imp_col_date')}</th>
                    <th style={th}>{t('aw_far_imp_col_name')}</th>
                    <th style={th}>{t('aw_far_imp_col_category')}</th>
                    <th style={{ ...th, textAlign: 'right' }}>{t('aw_far_imp_col_cost')}</th>
                    <th style={th}>{t('aw_far_imp_col_verdict')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map(r => (
                    <tr key={r.line} data-fa-imp-line={r.line} data-fa-imp-ok={r.error ? '0' : '1'}>
                      <td style={num}>{r.line}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.acquiredDate ? dmy(r.acquiredDate) : '—'}</td>
                      <td style={td}>
                        <div>{r.nameTh || '—'}</div>
                        {r.nameEn && r.nameEn !== r.nameTh && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{r.nameEn}</div>}
                      </td>
                      <td style={td}>
                        {r.category
                          ? r.category
                          : chosen && previewOf?.categoryId === chosen.id
                            ? <span>{catName(chosen)} <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>({t('aw_far_imp_for_file')})</span></span>
                            : '—'}
                      </td>
                      {/* SATANG from the server: baht() divides once. */}
                      <td style={num} data-fa-imp-cost="1">{baht(r.cost)}</td>
                      <td style={{ ...td, maxWidth: 520 }}>
                        {r.error
                          ? <><span style={r.duplicate ? pillAmber : pillRed}>✕</span> <span style={{ color: '#B42318', fontSize: 12 }}>{r.error}</span></>
                          : <span style={pillGreen}>{t('aw_far_imp_ok')}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} total={ordered.length} limit={limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />
          </div>
        )}
      </div>
    </Dialog>
  );
}
